import type { FrameworkAction, FrameworkArgument } from '../framework/framework.d';
import { resolveElectronicVariableClassName, type CollectElectronicVariableNamesForClassOpts } from './frameworkClassNameSets';

const AUTO_ZERO_WITH_SENSOR_ACTION = 'auto_zero_with_sensor_action';
const AUTO_ZERO_SENSOR_ARG = 'sensor';
const BINARY_SENSOR_CLASSES = new Set(['BeamBreakSensor', 'MagneticProximitySwitch']);
const DISTANCE_SENSOR_CLASS = 'DistanceSensor';
const BINARY_SENSOR_ARGUMENTS = new Set(['invert_sensor']);
const DISTANCE_SENSOR_ARGUMENTS = new Set(['distance_criteria', 'is_less_than']);
const SENSOR_SPECIFIC_ARGUMENTS = new Set([...BINARY_SENSOR_ARGUMENTS, ...DISTANCE_SENSOR_ARGUMENTS]);

export type AutoZeroSensorActionResolution = {
  actionDef: FrameworkAction;
  inapplicableArgumentNames: string[];
  sensorClassName: string;
};

/**
 * Narrows `auto_zero_with_sensor_action` metadata to the arguments applicable to the selected
 * configured sensor. Returns `null` when the selected source cannot be resolved safely.
 */
export function resolveAutoZeroSensorAction(
  actionDef: FrameworkAction | null | undefined,
  argValues: Record<string, string> | undefined,
  programCode: string,
  opts?: CollectElectronicVariableNamesForClassOpts
): AutoZeroSensorActionResolution | null {
  if (actionDef?.name !== AUTO_ZERO_WITH_SENSOR_ACTION) return null;
  const sensorArg = (actionDef.arguments ?? []).find((arg) => arg.name === AUTO_ZERO_SENSOR_ARG) as FrameworkArgument | undefined;
  if (!sensorArg) return null;

  const sensorClassName = resolveElectronicVariableClassName(programCode, argValues?.[AUTO_ZERO_SENSOR_ARG] ?? '', sensorArg.type ?? '', opts);
  const applicableSensorArguments = BINARY_SENSOR_CLASSES.has(sensorClassName ?? '')
    ? BINARY_SENSOR_ARGUMENTS
    : sensorClassName === DISTANCE_SENSOR_CLASS
    ? DISTANCE_SENSOR_ARGUMENTS
    : null;
  if (!sensorClassName || !applicableSensorArguments) return null;

  const inapplicableArgumentNames = [...SENSOR_SPECIFIC_ARGUMENTS].filter((name) => !applicableSensorArguments.has(name));
  const argumentsForSensor = (actionDef.arguments ?? []).filter((arg) => !inapplicableArgumentNames.includes(arg.name));
  return {
    actionDef: { ...actionDef, arguments: argumentsForSensor },
    inapplicableArgumentNames,
    sensorClassName,
  };
}

/**
 * Context needed to narrow `auto_zero_with_sensor_action` metadata to the selected sensor. `programCode`
 * is the source the sensor variable is declared in, matching what the argument fields render against.
 *
 * Required wherever it is accepted rather than optional: omitting it silently validates arguments the
 * fields never render, which is what permanently disabled the save button for binary sensors.
 */
export type AutoZeroSensorActionContext = {
  programCode: string;
  availableBeforeLineNumber?: number;
};

/**
 * Narrows `actionDef` to the arguments the argument fields actually render for the selected sensor,
 * returning it unchanged when the action is not sensor-dependent or the sensor cannot be resolved.
 * Validation must apply this so it never requires arguments that were dropped as inapplicable.
 */
export function withApplicableAutoZeroSensorArgs(
  actionDef: FrameworkAction | null | undefined,
  argValues: Record<string, string> | undefined,
  context: AutoZeroSensorActionContext
): FrameworkAction | null | undefined {
  const resolution = resolveAutoZeroSensorAction(actionDef, argValues, context.programCode, {
    availableBeforeLineNumber: context.availableBeforeLineNumber,
  });
  return resolution?.actionDef ?? actionDef;
}
